import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Button } from 'components/common';
import { DropdownButton } from 'components/common/dropdown';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalPortal
} from 'components/common/modal';

type ButtonProps = React.ComponentProps<typeof Button>;
type DropdownButtonProps = React.ComponentProps<typeof DropdownButton>;
export type ModalButtonRef = { hideModal: () => void };

type Props = React.PropsWithChildren & {
  getModalButton?: (modalButton: ModalButtonRef) => void;
  modalContentStyle?: React.CSSProperties;
  modalProps?: Partial<React.ComponentProps<typeof Modal>>;
  onModalHide?: () => void;
  onModalShow?: () => void;
} & (
    | ({ buttonIsDropdown: true } & DropdownButtonProps)
    | ({ buttonIsDropdown?: false } & ButtonProps)
  );

const ModalButtonContext = createContext<{
  setFooterChildren: (children: React.ReactNode) => void;
}>({ setFooterChildren: () => {} });

const ModalButton = ({
  buttonIsDropdown,
  children,
  getModalButton,
  modalContentStyle,
  modalProps,
  onModalHide,
  onModalShow,
  ...props
}: Props) => {
  const buttonElement = useRef<HTMLButtonElement>(undefined);

  const [modalVisible, setModalVisible] = useState(false);
  const [footerChildren, setFooterChildren] = useState<React.ReactNode>(null);

  const hideModal = () => {
    setModalVisible(false);
    setFooterChildren(null);

    setTimeout(() => {
      if (buttonElement.current) buttonElement.current.focus();
    });
    if (onModalHide) onModalHide();
  };

  const showModal: React.MouseEventHandler<HTMLButtonElement> = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();
    setModalVisible(true);
    if (onModalShow) onModalShow();
  };

  useEffect(() => {
    if (getModalButton) getModalButton({ hideModal });
  }, [getModalButton]);

  return (
    <ModalButtonContext.Provider value={{ setFooterChildren }}>
      {buttonIsDropdown ? (
        <DropdownButton onClick={showModal} ref={buttonElement} {...props} />
      ) : (
        <Button onClick={showModal} ref={buttonElement} {...props} />
      )}
      <ModalPortal>
        <Modal onHide={hideModal} visible={modalVisible} {...modalProps}>
          <ModalContent style={modalContentStyle}>{children}</ModalContent>
          {footerChildren && <ModalFooter>{footerChildren}</ModalFooter>}
        </Modal>
      </ModalPortal>
    </ModalButtonContext.Provider>
  );
};

const Footer = ({ children }) => {
  const { setFooterChildren } = useContext(ModalButtonContext);

  useEffect(() => {
    setFooterChildren(children);
    return () => {
      setFooterChildren(null);
    };
  }, [children, setFooterChildren]);

  return null;
};

ModalButton.Footer = Footer;

export default ModalButton;
